import { Button, Col, Dropdown, Input, notification, Row, Skeleton, Space } from "antd";
import { useEffect, useState, useRef } from "react";
import { useRecoilValue } from "recoil";
import { getEnvironmentService } from "../../backend";
import { currentProjectState } from "../../recoil/project";
import { Environment, EnvironmentDetails } from "../../types";
import { DownOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CreateStageRequestCloudProviderEnum, CreateStageRequestInfrastructureAsCodeToolEnum } from "@microtica/ms-engine-sdk";
import { trackEnvCreateSucceeded, trackEnvCreateFailed } from "../../backend/tracking/environment";
import { getEnvironmentsLimit } from "../../utils/local-storage";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { InputRef } from 'antd/lib/input';

interface EnvironmentSelectorProps {
    disabled?: boolean;
    selectedEnv?: EnvironmentDetails;
    onSelect?: (environment?: EnvironmentDetails) => void;
}

const EnvironmentSelect = ({
    disabled,
    selectedEnv,
    onSelect
}: EnvironmentSelectorProps) => {
    const newEnvNameRef = useRef<InputRef>(null);
    const newEnvDescriptionRef = useRef<InputRef>(null);
    const [newEnvironmentError, setNewEnvironmentError] = useState({ nameError: false, descriptionError: false });
    const currentProject = useRecoilValue(currentProjectState);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [mode, setMode] = useState<"new" | "existing">("existing");
    const [enableCreateEnvironment, setEnableCreateEnvironment] = useState(true);
    const [loadingEnvs, setLoadingEnvs] = useState(true);
    const [creatingEnv, setCreatingEnv] = useState(false);

    const loadEnvironments = async () => {
        try {
            const { data: { stages: environments } } = await getEnvironmentService().getStages(currentProject!.id);
            const environmentsLimit = getEnvironmentsLimit();
            if (environmentsLimit <= environments.length) {
                setEnableCreateEnvironment(false);
            }
            const defaultEnv = environments.length === 1 ? environments[0] : undefined;

            if (!selectedEnv && defaultEnv) {
                onSelect?.({
                    id: defaultEnv.id,
                    name: defaultEnv.name,
                    accountId: defaultEnv.awsAccountId,
                    region: defaultEnv.awsRegion,
                    cloudProvider: defaultEnv.cloudProvider
                });
            }

            setEnvironments(environments.map(env => ({
                id: env.id,
                name: env.name,
                cloudProvider: env.cloudProvider,
                accountId: env.awsAccountId,
                region: env.awsRegion
            })));
            setMode(environments.length > 0 ? "existing" : "new");

        } catch (error) {

        } finally {
            setLoadingEnvs(false);
        }
    }

    useEffect(() => {
        loadEnvironments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mode === "new") {
            onSelect?.();
        } else {
            if (!selectedEnv && environments.length === 1) {
                // onSelect() must be explicitely called because the hook for selectedEnvironment is not triggered if environments[0] === selectedEnvironment
                onSelect?.(environments[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const handleCreateEnvironment = async () => {
        const name = newEnvNameRef?.current?.input?.value;
        const description = newEnvDescriptionRef?.current?.input?.value;

        try {
            if (!name || !description) {
                setNewEnvironmentError({
                    nameError: !name,
                    descriptionError: !description
                });
                return;
            }
            setNewEnvironmentError({
                nameError: false,
                descriptionError: false
            });

            setCreatingEnv(true);
            const { data: { id } } = await getEnvironmentService().createStage(
                currentProject!.id,
                {
                    name: name!,
                    description: description!,
                    cloudProvider: CreateStageRequestCloudProviderEnum.Aws,
                    infrastructureAsCodeTool: CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation
                });
            await loadEnvironments();
            setMode("existing");
            onSelect?.({
                id,
                name,
                cloudProvider: CreateStageRequestCloudProviderEnum.Aws
            });
            notification.success({ message: "New environment created" });
            trackEnvCreateSucceeded(
                id,
                name!,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false
            );
        } catch (error: any) {
            notification.error({
                message: "Cannot create new environment",
                description: error.response.data.message
            });
            trackEnvCreateFailed(
                name!,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false,
                error.response.data.message
            );
        } finally {
            setCreatingEnv(false);
        }
    }

    return (
        loadingEnvs ?
            <Skeleton /> :
            mode === "new" ?
                // New environment
                <div>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Input
                                placeholder="development"
                                ref={newEnvNameRef}
                                disabled={disabled}
                                required
                                className={newEnvironmentError.nameError ? "error-field-wrapper" : ""}
                            />
                            {
                                newEnvironmentError.nameError &&
                                <span style={{ color: "#ff4d4f" }}>
                                    Enter environment name
                                </span>
                            }
                        </Col>
                        <Col span={12}>
                            <Input
                                placeholder="My development environment"
                                maxLength={100}
                                ref={newEnvDescriptionRef}
                                showCount
                                disabled={disabled}
                                required
                                className={newEnvironmentError.descriptionError ? "error-field-wrapper" : ""}
                            />
                            {
                                newEnvironmentError.descriptionError &&
                                <span style={{ color: "#ff4d4f" }}>
                                    Enter environment description
                                </span>
                            }
                        </Col>
                    </Row>
                    <br />
                    <Row>
                        <Col span={12} style={{ textAlign: "left" }}>
                            {
                                environments.length > 0 &&
                                <Button type="link" style={{ padding: 0 }} onClick={() => {
                                    setMode("existing");
                                }}>
                                    Choose existing environment
                                </Button>
                            }
                        </Col>
                        <Col span={12} style={{ textAlign: "right" }}>
                            <Button
                                type="primary"
                                onClick={handleCreateEnvironment}
                                loading={creatingEnv}
                                disabled={disabled}>
                                Create
                            </Button>
                        </Col>
                    </Row>
                </div>
                :
                // Existing environment
                <>
                    <Space>
                        Select Environment:
                        <Dropdown
                            trigger={["click"]}
                            placement="bottomRight"
                            menu={{
                                placeholder: selectedEnv?.id || "Select environment",
                                items: environments
                                    .map(env => ({
                                        key: env.id,
                                        label: env.name,
                                        onClick: () => onSelect?.(env)
                                    }) as ItemType)
                                    .concat(enableCreateEnvironment ? [
                                        { type: "divider" },
                                        {
                                            key: "create-environment",
                                            label: <><PlusCircleOutlined /> Create Environment</>,
                                            onClick: () => setMode("new")
                                        }
                                    ] : [])
                            }}
                            disabled={disabled}
                        >
                            <Button>
                                {selectedEnv ? selectedEnv.name : "Select environment"} <DownOutlined />
                            </Button>
                        </Dropdown>
                    </Space>
                </>
    );
};


export default EnvironmentSelect;